import { isCostUnit, isUnitB, isCO2eUnit } from '@shared/helpers/unit.helpers';
import { isDefined } from '@shared/helpers/array.helpers';
import { Results } from '@shared/models/unit.model';
import { orderBy, size, sortBy } from 'lodash';
import { ISimpleBarChartValue } from '../SimpleBarChart';
import { ISimpleBarChartTooltipData } from '@features/projects/charts/SimpleBarChartTooltip';
import { lifecycleLabels } from '@shared/models/lifecycles.model';
import {
  getCO2eUnitsFromLifecycle,
  getCostUnitsFromLifecycle,
} from '@shared/helpers/lifecycles.helpers';
import { sum } from '@shared/helpers/math.helpers';

const OPACITY_B = 0.7;

const getTooltipData = (
  key: string,
  results: Results,
): ISimpleBarChartTooltipData => {
  const lifecycle =
    isCO2eUnit(key) || isCostUnit(key) ? lifecycleLabels[key] : undefined;

  if (!lifecycle) return { label: '', co2e: 0, sek: 0 };

  // Sum all units in the lifecycle since B1 has two co2e sub-units and B1/B7 have no cost unit
  const co2e = sum(
    getCO2eUnitsFromLifecycle(lifecycle).map((unit) => results[unit] ?? 0),
  );
  const sek = sum(
    getCostUnitsFromLifecycle(lifecycle).map((unit) => results[unit] ?? 0),
  );
  return {
    label: lifecycle,
    co2e,
    sek,
  };
};

export const getCo2eChartDataFromResults = (
  results: Results,
): ISimpleBarChartValue[] => {
  return sortBy(
    Object.entries(results)
      .map(([key, value]) => {
        const data: ISimpleBarChartValue = {
          key,
          value,
          opacity: isUnitB(key) ? OPACITY_B : 1,
          tooltipData: getTooltipData(key, results),
        };
        return isCO2eUnit(key) ? data : undefined;
      })
      .filter(isDefined),
    'key',
  );
};

export const getCostChartDataFromResults = (
  results: Results,
): ISimpleBarChartValue[] => {
  return sortBy(
    Object.entries(results)
      .map(([key, value]) =>
        isCostUnit(key)
          ? { label: key, value, opacity: isUnitB(key) ? OPACITY_B : 1 }
          : undefined,
      )
      .filter(isDefined),
    'key',
  );
};

/** CO₂ segments with non-positive values (e.g. B1.1 carbonation uptake) are omitted from bar geometry. */
export const isPositiveChartBarValue = (value: number): boolean =>
  Number.isFinite(value) && value > 0;

/**
 * Get the width of a bar in percent.
 * When {@link maxValue} is 0 (e.g. all proposals have zero cost/CO2), avoid division by zero so the bar is not shown as full width.
 * Negative values (e.g. `co2e_B1.1` carbonation uptake) return 0 — they still affect totals/tooltips but not segment size.
 * @param value
 * @param maxValue
 * @returns
 */
export const getWidthInPercent = (value: number, maxValue: number): number => {
  if (!isPositiveChartBarValue(value)) return 0;
  if (!Number.isFinite(maxValue) || maxValue <= 0) return 0;
  return Math.min(100, (value / maxValue) * 100);
};

export type HBarWidthPercents = {
  co2Pct: number;
  costPct: number;
  baselinePct: number;
};

export type GetHBarWidthPercentsParams = {
  co2Value: number;
  costValue: number;
  maxCo2: number;
  maxCost: number;
  baselineCo2Value?: number;
};

/**
 * Width percentages for the element-list horizontal bar (CO₂, optional baseline, cost).
 * Uses {@link getWidthInPercent} so negative CO₂ (e.g. B1.1 carbonation) and `maxCo2 === 0`
 * never produce a visible bar width.
 */
export const getHBarWidthPercents = ({
  co2Value,
  costValue,
  maxCo2,
  maxCost,
  baselineCo2Value,
}: GetHBarWidthPercentsParams): HBarWidthPercents => ({
  co2Pct: getWidthInPercent(co2Value, maxCo2),
  costPct: getWidthInPercent(costValue, maxCost),
  baselinePct:
    baselineCo2Value != null ? getWidthInPercent(baselineCo2Value, maxCo2) : 0,
});

export const CO2_STACK_REMAINDER_KEY = '__co2e_remainder';

export type VerticalCo2Segment = ISimpleBarChartValue & {
  segHeight: number;
  segBottom: number;
};

/**
 * Build stacked vertical CO₂ bar segments from per-lifecycle chart rows.
 * Omits non-positive segments (B1.1 carbonation) and never assigns negative height.
 */
export const buildVerticalCo2Segments = (
  co2eChartData: ISimpleBarChartValue[],
  co2ePerGFA: number,
  innerBarHeight: number,
): VerticalCo2Segment[] => {
  if (!isPositiveChartBarValue(co2ePerGFA) || innerBarHeight <= 0) return [];

  const phaseSegments = co2eChartData.filter((seg) =>
    isPositiveChartBarValue(seg.value),
  );
  const phaseSum = phaseSegments.reduce((acc, seg) => acc + seg.value, 0);
  const remainder = Math.max(0, co2ePerGFA - phaseSum);
  const stackDenominator = Math.max(co2ePerGFA, phaseSum, 1e-15);

  const extended: ISimpleBarChartValue[] =
    remainder > 1e-9
      ? [
          ...phaseSegments,
          {
            key: CO2_STACK_REMAINDER_KEY,
            value: remainder,
            tooltipData: {
              label: 'Other CO₂e',
              co2e: remainder,
              sek: 0,
            },
          },
        ]
      : phaseSegments;

  let accumulated = 0;
  return extended.map((seg) => {
    const segHeight = Math.max(
      0,
      (seg.value / stackDenominator) * innerBarHeight,
    );
    const segBottom = accumulated;
    accumulated += segHeight;
    return { ...seg, segHeight, segBottom };
  });
};

/**
 * Convert the different types of input values to an array of bar chart values.
 * @param value
 * @returns
 */
export const toBarChartValues = (
  value:
    number | ISimpleBarChartValue[] | number[] | Record<string, number> = 0,
): ISimpleBarChartValue[] => {
  // Handle empty values
  if (typeof value === 'number') {
    return [{ value }];
  }
  // Handle empty values
  if (size(value) === 0) {
    return [{ value: 0 }];
  }
  if (Array.isArray(value)) {
    return sortBy(
      value.map((v) => (typeof v === 'number' ? { value: v } : v)),
      'label',
    );
  }

  return orderBy(
    Object.entries(value).map(([label, value]) => ({ label, value })),
    ['label', 'value'],
    ['asc', 'desc'],
  );
};

/**
 * Remove empty values from the bar chart values.
 * Make sure to keep at least one value even if it's 0.
 * @param values
 * @returns
 */
export const removeEmptyValues = (
  values: ISimpleBarChartValue[],
): ISimpleBarChartValue[] => {
  const firstValue = values[0];
  if (!firstValue) {
    return [];
  }
  const filtered = values.filter((v) => isPositiveChartBarValue(v.value));
  return filtered.length > 0 ? filtered : [{ ...firstValue, value: 0 }];
};
